import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import { brokerOverviewQueryOptions, sectorFlowQueryOptions } from '@/features/brokers/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import { overviewSearchSchema } from '@/screens/brokers/search'

/**
 * The `/brokers` loader.
 *
 * MAIN: the overview, keyed on the `period` search param — `FloorStatsStrip`
 * and `TopBrokersCard` both read this one query, so a failed first load
 * blanks the whole page the same way Market's does.
 *
 * SECONDARY, started before the `await` and only on a real navigation (not a
 * hover preload — docs/route-loading.md, "Secondary reads"): sector flow for
 * "Sectors traded most", which draws its own skeleton and failure, so it
 * cannot blank the page. Its per-row name reads are not started here: which
 * three sectors to ask about is sector flow's answer, so the card starts them
 * once it lands. "Stocks traded most" has no served read yet
 * (`STOCKS_TRADED_SIDES_AVAILABLE`), so nothing is prefetched for it.
 *
 * `period` is read from `location.search` through the route's own schema, not
 * through `loaderDeps` — see docs/route-loading.md, "Search params". Because
 * main data here is keyed on a search param, the screen does NOT read it with
 * a suspense query: `useBrokerOverview` stays the plain `useQuery` +
 * `keepPreviousData` it already was, so a future period control can change the
 * URL without a route-level pending skeleton. This loader only makes sure the
 * ENTRY url's period is in the cache before the page mounts.
 *
 * Its own module, as `app.guard.ts` is, so a test can mount it under a root
 * of its own: typed inside `createFileRoute`, a loader is bound to this
 * route's parent in the generated tree. It imports from `api/` and route
 * search-schema modules only — route loaders are not code-split, so anything
 * imported here ships in the entry chunk.
 */
export async function loadBrokersOverview({
  context: { queryClient },
  location,
  preload,
}: {
  context: RouterContext
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  const { period } = overviewSearchSchema.parse(location.search)
  if (!preload) {
    prefetchRouteData(queryClient, sectorFlowQueryOptions(period))
  }
  await ensureRouteData(queryClient, brokerOverviewQueryOptions(period))
}
